/* eslint-disable */
import { t } from 'i18next';
import React, { RefObject, createRef, forwardRef, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Editor } from '@ckeditor/ckeditor5-core';
import { Element } from '@ckeditor/ckeditor5-engine';
import ModelItem from '@ckeditor/ckeditor5-engine/src/model/item';
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
import RootElement from '@ckeditor/ckeditor5-engine/src/model/rootelement';
import Writer from '@ckeditor/ckeditor5-engine/src/model/writer';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { Item, ItemParams, Menu, Separator, Submenu, useContextMenu } from 'react-contexify';
import 'react-contexify/ReactContexify.css';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import Modal from '../../../../modules/Modal';
import { AttributeDialog } from './AttributeDialog';
import { ElementDefinition } from './Definition';
import { Droppable } from './Droppable';
import Schema from './Schema';
import { SectionDataTreeNode } from './SectionDataTreeNode';
import { UpdateElementHandler } from './SectionDataTreeNodeInterface';
import { TextAttributeDialog } from './TextAttributeDialog';
import { ValidationResults } from './ValidationResults';

interface DataTreeRootNodeProps {
  editor: Editor;
  rootElement: RootElement;
  selectedElement?: Node;
  setSelectedElement: (selectedElement?: Node) => void;
  expandingItems: Element[];
  setExpandingItems: (expandingItems: Element[]) => void;
  viewAttributes: string[];
  scrollIntoElement: Node | undefined;
  resetScrollIntoElement: () => void;
  validationResults: ValidationResults;
  isPs?: boolean;
}

interface Attribute {
  on: boolean;
  name: string;
  value: string;
  parentName: string;
}

interface Tag {
  name: string;
  definition: ElementDefinition;
  attributes: Attribute[];
}

const SectionDataTreeRootNode = forwardRef<UpdateElementHandler, DataTreeRootNodeProps>(
  (
    {
      editor,
      rootElement,
      selectedElement,
      setSelectedElement,
      expandingItems,
      setExpandingItems,
      viewAttributes,
      scrollIntoElement,
      resetScrollIntoElement,
      validationResults,
      isPs,
    },
    ref
  ) => {
    const id = useId();

    const { show } = useContextMenu({
      id,
    });

    /**
     * 更新用ステート変数。
     */
    const [counter, setCounter] = useState<number>(0);
    const updateComponentLocal = () => {
      setCounter(counter + 1);
    };

    // Element型として必要な場合
    const element = rootElement as unknown as Element;

    /**
     * 要素の選択クリックイベントハンドラ。
     * @param e イベントパラメータ。
     */
    const handleSelectClick = (node: Node, e: React.MouseEvent) => {
      // 選択の変化を上位コンポーネントに伝える
      setSelectedElement(node);
    };

    const childElements: Array<any> = Array.from(rootElement.getChildren());
    const childElementRefs = useRef<RefObject<UpdateElementHandler>[]>([]);
    childElements.forEach((_, index) => {
      childElementRefs.current[index] = createRef<UpdateElementHandler>();
    });

    /**
     * 展開・閉じるボタンのクリックイベントハンドラ。
     * @param e イベントパラメータ。
     */
    const handleToggleClick = (e: React.MouseEvent) => {
      if (!expandingItems.includes(element)) {
        expandingItems.push(element);
      } else {
        expandingItems = expandingItems.filter((x) => x != element);
      }
      setExpandingItems(expandingItems.concat());
    };

    /**
     * コンテキストメニュー用ノード。
     */
    const [menuTargetNode, setMenuTargetNode] = useState<Node | undefined>(undefined);

    /**
     * コンテキストメニューのコピーが可能かどうかのフラグ。
     */
    const [isCopyable, setIsCopyable] = useState<boolean>(false);

    /**
     * コンテキストメニューを表示する。
     * @param node 対象ノード。
     * @param e マウスイベントパラメータ。
     */
    const showContextMenu = async (node: Node, e: React.MouseEvent) => {
      e.preventDefault();

      // メニュー対象ノードを設定（確実に反映させるために以下のようにしている）
      setMenuTargetNode(undefined);
      setMenuTargetNode(node);

      let copyable = false;
      if (node.is('model:element')) {
        const targetElement = node as Element;
        switch (targetElement.name) {
          // case 'l2Section':  // L2セクションはコピー不可
          case 'l3Section':
          case 'l4Section':
          case 'l5Section':
            copyable = true;
            break;

          default:
            if (isInDivContents(targetElement) || isDivContents(targetElement)) {
              copyable = true;
            } else if (isDivTitle(targetElement)) {
              copyable = true;
            }
            break;
        }
      }
      setIsCopyable(copyable);

      if (selectedElement !== node) {
        setSelectedElement(node);
      }

      // ちょっとずらさないと表示されないときがある
      setTimeout(() => {
        show({
          event: e,
        });
      }, 400);
    };

    /**
     * 右クリックイベントハンドラ（onContextMenu）。
     * @param e イベントパラメータ。
     */
    const onContextMenu = async (e: React.MouseEvent) => {
      await showContextMenu(rootElement, e);
    };

    // 配列に指定要素とその配下の要素をすべて追加
    const addElement = (array: Element[], target: Element) => {
      if (target.is('$text')) {
        return;
      }

      if (!array.includes(target)) {
        array.push(target);
      }

      const childElements = Array.from(target.getChildren()).filter((child) => !child.is('$text')) as Element[];
      childElements.forEach((child) => {
        addElement(array, child);
      });
    };

    /**
     * すべてを展開コンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handleExpandAllItemClick = (args: ItemParams) => {
      addElement(expandingItems, menuTargetNode as Element);
      setExpandingItems(expandingItems.concat());
    };

    // 配列から指定要素とその配下の要素をすべて削除
    const removeElement = (array: Element[], target: Element) => {
      if (target.is('$text')) {
        return;
      }

      let index: number;
      while ((index = array.lastIndexOf(target)) >= 0) {
        array.splice(index, 1);
      }

      const childElements = Array.from(target.getChildren()).filter((child) => !child.is('$text')) as Element[];
      childElements.forEach((child) => {
        removeElement(array, child);
      });
    };

    /**
     * すべてを閉じるコンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handleCollapseAllItemClick = (args: ItemParams) => {
      removeElement(expandingItems, menuTargetNode as Element);
      setExpandingItems(expandingItems.concat());
    };

    // コピーしたJSON上に埋め込まれる情報
    const CLIPBOARD_DATA_TYPE = 'ckeditor5.element';
    const CLIPBOARD_DATA_TYPE_PS = 'ckeditor5.element.ps';

    /**
     * オブジェクト内の特定の名前の項目を処理する。
     * @param obj 対象オブジェクト。
     * @param name 名前。
     * @param process 処理関数。
     */
    const processProperty = (obj: any, name: string, process: (obj: any, name: string) => void): void => {
      Object.keys(obj).forEach((key) => {
        if (key === name) {
          process(obj, key);
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          processProperty(obj[key], name, process);
        }
      });
    };

    /**
     * 削除確認ダイアログ表示フラグ。
     */
    const [isDeleteConfirmDialogVisible, setIsDeleteConfirmDialogVisible] = useState<boolean>(false);

    /**
     * 削除コンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handleDeleteItemClick = (args: ItemParams<any, any>) => {
      // 削除確認ダイアログを表示
      setIsDeleteConfirmDialogVisible(true);
    };

    /**
     * 削除確認ダイアログで「Yes」が押されたときに呼ばれる。
     */
    const handleDeleteConfirm = () => {
      if (!menuTargetNode) {
        return;
      }

      const parentElement = menuTargetNode.parent as Element;

      editor.model.change((writer: Writer) => {
        writer.remove(menuTargetNode);
      });

      // 親要素を選択する（自動的に再読込する）
      setSelectedElement(parentElement);
    };

    /**
     * Comp対象要素かどうか。
     */
    const isCompElement = useMemo(() => {
      if (!menuTargetNode) {
        return false;
      }

      if (!menuTargetNode.is('model:element')) {
        return false;
      }

      const targetElement = menuTargetNode as Element;

      // セクション系ノードでなければ、対象ノードだけを追加して返す
      return targetElement.name.match(/^l[2-5]Section$/) ? true : false;
    }, [menuTargetNode]);

    /**
     * カットコンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handleCompCutItemClick = (args: ItemParams<any, any>) => {
      if (!menuTargetNode) {
        return;
      }

      // コピーしてから
      handleCompCopyItemClick(args);

      const parentElement = menuTargetNode.parent as Element;
      if (isCompElement) {
        const targetElement = menuTargetNode as Element;
        const targetElements = getCompElements(targetElement);
        editor.model.change((writer: Writer) => {
          targetElements.forEach((current) => {
            writer.remove(current);
          });
        });
      } else {
        editor.model.change((writer: Writer) => {
          writer.remove(menuTargetNode);
        });
      }

      // 親要素を選択する（自動的に再読込する）
      setSelectedElement(parentElement);
    };

    /**
     * コピーコンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handleSingleCopyItemClick = (args: ItemParams<any, any>) => {
      if (!menuTargetNode) {
        return;
      }

      // コピーデータのPS種別を設定
      var oriType = 0;
      if (isInDivTitle(menuTargetNode as Element)) {
        oriType = PSTYPE_TITLE;
      } else if (isInDivContents(menuTargetNode as Element)) {
        oriType = PSTYPE_CONTENTS;
      }

      // コピーデータを作成
      const copyData = { type: CLIPBOARD_DATA_TYPE, psType: oriType, data: [] as any[] };
      let currentElementData = JSON.stringify(menuTargetNode);
      const tempElement = JSON.parse(currentElementData);
      // idを削除
      processProperty(tempElement, 'id', (obj, name) => {
        delete obj[name];
      });
      // section_idの先頭に#を付与
      processProperty(tempElement, 'section_id', (obj, name) => {
        obj[name] = `#${obj[name]}`;
      });
      copyData.data.push(tempElement);

      // blob用JSON文字列作成
      const blobData = JSON.stringify(copyData);

      navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([blobData], { type: 'text/plain' }),
        }),
      ]);
    };

    /**
     * 範囲コピーコンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handleCompCopyItemClick = (args: ItemParams<any, any>) => {
      if (!menuTargetNode) {
        return;
      }
      if (!menuTargetNode.is('model:element')) {
        return;
      }

      // 対象要素リストを取得
      const targetElements = getCompElements(menuTargetNode as Element);

      // コピーデータを作成
      const copyData = { type: CLIPBOARD_DATA_TYPE, psType: -1, data: [] as any[] };
      targetElements.forEach((current) => {
        let currentElementData = JSON.stringify(current);
        const tempElement = JSON.parse(currentElementData);
        // idを削除
        processProperty(tempElement, 'id', (obj, name) => {
          delete obj[name];
        });
        // section_idの先頭に#を付与
        processProperty(tempElement, 'section_id', (obj, name) => {
          obj[name] = `#${obj[name]}`;
        });
        copyData.data.push(tempElement);
      });

      // blob用JSON文字列作成
      const blobData = JSON.stringify(copyData);

      navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([blobData], { type: 'text/plain' }),
        }),
      ]);
    };

    /**
     * Comp要素リストを取得。l[2-5]Section要素が対象で、それ以外は引数要素だけを入れて返す。
     * @param targetElement 起点要素。
     * @returns 要素配列。
     */
    const getCompElements = (targetElement: Element): Element[] => {
      // セクション系ノードでなければ、対象ノードだけを追加して返す
      if (!targetElement.name.match(/^l[2-5]Section$/)) {
        return [targetElement];
      }

      const regMatch = targetElement.name.match(/\d+/);
      if (!regMatch) {
        return [targetElement];
      }

      const stopElementNames = [] as string[];
      const targetSectionNumber = parseInt(regMatch[0]);
      for (let i = 2; i <= targetSectionNumber; i++) {
        stopElementNames.push(`l${i}Section`);
      }

      const data = [] as Element[];
      let currentTargetElement = targetElement;
      while (currentTargetElement) {
        data.push(currentTargetElement);

        // 次の要素を取得
        currentTargetElement = currentTargetElement.nextSibling as Element;
        if (!currentTargetElement) {
          break;
        }
        // ストップ要素の場合は抜ける
        if (stopElementNames.includes(currentTargetElement.name)) {
          break;
        }
      }

      return data;
    };

    /**
     * 指定された要素がdiv.contents配下（自身を含む）にあるかどうかを返す。
     * @param targetElement 対象要素。
     * @returns div.contents配下にあればtrue。
     */
    const isInDivContents = (targetElement: Element | null | undefined): boolean => {
      if (!targetElement) {
        return false;
      }

      if (isDivContents(targetElement)) {
        return true;
      }

      return isInDivContents(targetElement.parent as Element | null);
    };

    /**
     * 指定された要素がdiv.contentsかどうかを返す。
     * @param targetElement 対象要素。
     * @returns div.contentsであればtrue。
     */
    const isDivContents = (targetElement: Element | null | undefined): boolean => {
      if (!targetElement) {
        return false;
      }

      if (targetElement.name !== 'htmlDiv') {
        return false;
      }

      const htmlDivAttributes = targetElement.getAttribute('htmlDivAttributes') as any;
      if (Array.isArray(htmlDivAttributes?.classes)) {
        const classes = htmlDivAttributes?.classes as string[];
        if (classes.includes('contents')) {
          return true;
        }
      }

      return false;
    };

    /**
     * 指定された要素がdiv.title配下（自身を含む）にあるかどうかを返す。
     * @param targetElement 対象要素。
     * @returns div.title配下にあればtrue。
     */
    const isInDivTitle = (targetElement: Element | null | undefined): boolean => {
      if (!targetElement) {
        return false;
      }

      if (isDivTitle(targetElement)) {
        return true;
      }

      return isInDivTitle(targetElement.parent as Element | null);
    };

    /**
     * 指定された要素がdiv.titleかどうかを返す。
     * @param targetElement 対象要素。
     * @returns div.titleであればtrue。
     */
    const isDivTitle = (targetElement: Element | null | undefined): boolean => {
      if (!targetElement) {
        return false;
      }

      if (targetElement.name !== 'htmlDiv') {
        return false;
      }

      const htmlDivAttributes = targetElement.getAttribute('htmlDivAttributes') as any;
      if (Array.isArray(htmlDivAttributes?.classes)) {
        const classes = htmlDivAttributes?.classes as string[];
        if (classes.includes('title')) {
          return true;
        }
      }

      return false;
    };

    /**
     * 指定された要素がdiv@section_idかどうかを返す。
     * @param targetElement 対象要素。
     * @returns div@section_idであればtrue。
     */
    const isDivSectionId = (targetElement: Element | null | undefined): boolean => {
      if (!targetElement) {
        return false;
      }

      if (targetElement.name !== 'htmlDiv') {
        return false;
      }

      const htmlDivAttributes = targetElement.getAttribute('htmlDivAttributes') as any;
      return htmlDivAttributes?.attributes?.section_id as boolean;
    };

    /**
     * クリップボードからペーストデータを読み込む。
     */
    const [clipboardText, setClipboardText] = useState<string>('');
    try {
      navigator.clipboard
        .readText()
        .then((text) => {
          setClipboardText(text);
        })
        .catch(() => {
          setClipboardText('');
        });
    } catch {
      // クリップボード読み込みエラー
      setClipboardText('');
    }

    /**
     * PSデータの種類。
     */
    const PSTYPE_ROOT = 0;
    const PSTYPE_CHAPTER = 1;
    const PSTYPE_TITLE = 2;
    const PSTYPE_CONTENTS = 3;

    /**
     * クリップボードにペーストデータが存在するかをチェックする。
     */
    const [isPastableAsChild, isPastableAsSibling] = useMemo(() => {
      try {
        const data = JSON.parse(clipboardText);

        if (data.type == CLIPBOARD_DATA_TYPE) {
          if (!Array.isArray(data.data)) {
            return [false, false];
          }
          if (data.data.length === 0) {
            return [false, false];
          }

          const elements = data.data as any[];
          let inRootElements = true;
          let inContentsElements = true;
          let inDivSectionIdElements = true;

          elements.forEach((current) => {
            switch (current.name) {
              // セクション系はdiv.contents配下はペースト不可
              case 'l2Section':
              case 'l3Section':
              case 'l4Section':
              case 'l5Section':
              case 'pageBreak':
                inContentsElements = false;
                inDivSectionIdElements = false;
                break;

              case 'htmlDiv':
                // div.title, div.contentsの場合はdiv@section_id配下に置ける要素
                const htmlDivAttributes = current.attributes.htmlDivAttributes as any;
                if (Array.isArray(htmlDivAttributes?.classes)) {
                  const classes = htmlDivAttributes?.classes as string[];
                  if (classes.includes('title') || classes.includes('contents')) {
                    inRootElements = false;
                    inContentsElements = false;
                    inDivSectionIdElements = true;
                  }
                }
                // それ以外はdiv.contents配下以外はペースト不可
                else {
                  inRootElements = false;
                  inDivSectionIdElements = false;
                }
                break;

              // 上記以外はdiv.contents配下以外はペースト不可
              default:
                inRootElements = false;
                inDivSectionIdElements = false;
                break;
            }
          });

          let canPasteAsChild = false;
          let canPasteAsSibling = false;
          if (menuTargetNode?.is('model:rootElement') && inRootElements) {
            canPasteAsChild = true;
          } else if (menuTargetNode?.parent?.is('model:rootElement') && inRootElements) {
            canPasteAsSibling = true;
          } else if (isDivSectionId(menuTargetNode as Element | null) && inDivSectionIdElements) {
            canPasteAsChild = true;
            canPasteAsSibling = false;
          } else if (isDivSectionId(menuTargetNode?.parent as Element | null) && inDivSectionIdElements) {
            canPasteAsChild = false;
            canPasteAsSibling = true;
          } else if (isDivContents(menuTargetNode as Element | null) && inContentsElements) {
            canPasteAsChild = true;
            canPasteAsSibling = false;
          } else if (isInDivContents(menuTargetNode?.parent as Element | null) && inContentsElements) {
            canPasteAsChild = true;
            canPasteAsSibling = true;
          }

          return [canPasteAsChild, canPasteAsSibling];
        } else if (data.type == CLIPBOARD_DATA_TYPE_PS) {
          if (!Array.isArray(data.data)) {
            return [false, false];
          }
          // PSデータは1つのみ
          if (data.data.length !== 1) {
            return [false, false];
          }

          switch (data.psType) {
            case PSTYPE_TITLE:
              if (isDivSectionId(menuTargetNode as Element | null)) {
                return [true, false];
              } else if (isDivSectionId(menuTargetNode?.parent as Element | null)) {
                return [false, true];
              }
              break;

            case PSTYPE_CONTENTS:
              if (isDivContents(menuTargetNode as Element | null)) {
                return [true, false];
              } else if (isDivContents(menuTargetNode?.parent as Element | null)) {
                return [false, true];
              }
              break;
          }
        }

        return [false, false];
      } catch (error) {
        return [false, false];
      }
    }, [clipboardText.length, clipboardText, menuTargetNode]);

    /**
     * ラップメニューが表示可能かどうかのフラグ。
     */
    const isWrapable = useMemo(() => {
      if (!menuTargetNode) {
        return false;
      }

      if (!menuTargetNode.is('model:element')) {
        return false;
      }

      if (menuTargetNode.is('model:rootElement')) {
        return false;
      }

      // ルート要素直下はラップ不可
      if (menuTargetNode?.parent?.is('model:rootElement')) {
        return false;
      }

      const name = Schema.getNameForValidation(menuTargetNode as Element);
      if (!name) {
        return false;
      }

      // divはラップ不可（ただしdiv.contents直下はラップ可能）
      if (/^div/.test(name)) {
        if (isDivContents(menuTargetNode.parent as Element)) {
          return true;
        }
        return false;
      }

      // div.title配下はラップ不可
      if (isInDivTitle(menuTargetNode as Element)) {
        return false;
      }

      return true;
    }, [menuTargetNode]);

    /**
     * メニュー表示フラグを取得。
     */
    const menuFlags = useMemo(() => {
      // 属性編集可能かどうか
      let attributeEditable = false;
      if (menuTargetNode?.is('$text')) {
        attributeEditable = true;

        // インデックス用のspan要素の場合は属性編集不可
        const htmlSpanAttribute = menuTargetNode?.getAttribute('htmlSpan');
        if (htmlSpanAttribute) {
          if ((htmlSpanAttribute as any).classes?.includes('indexterm')) {
            attributeEditable = false;
          }
        }
      } else if (menuTargetNode?.is('model:element')) {
        if (
          [
            'paragraph',
            'paragraphRefer',
            'paragraphRefer1',
            'paragraphReferGear',
            'warningListTitle',
            'paragraphSupplementSubhead',
            'paragraphSupplementPara',
            'heading1',
            'heading2',
            'heading3',
            'heading4',
            'heading5',
          ].includes(menuTargetNode.name)
        ) {
          // 段落や見出しの場合は属性編集可能
          attributeEditable = true;
        } else if (isDivSectionId(menuTargetNode as Element)) {
          // div@section_idの場合は属性編集可能
          attributeEditable = true;
        } else if (isDivTitle(menuTargetNode as Element)) {
          // div.titleの場合は属性編集可能
          attributeEditable = true;
        } else if (['htmlDiv'].includes(menuTargetNode.name)) {
          // ps_no属性がある場合は属性編集可能
          const htmlDivAttributes = menuTargetNode.getAttribute('htmlDivAttributes') as unknown as any;
          if (htmlDivAttributes?.attributes?.ps_no) {
            attributeEditable = true;
          }
        }
      }

      return {
        // 属性編集メニュー
        editAttributes: attributeEditable,
        // カットメニュー
        cut: !editor.isReadOnly && isCopyable,
        // コピーメニュー
        copy: isCopyable,
        // ペーストメニュー
        paste: !editor.isReadOnly && (isPastableAsChild || isPastableAsSibling),
        // 削除メニュー
        delete: !menuTargetNode?.is('model:rootElement'),
        // 展開・閉じるメニュー
        expandCollapse: !menuTargetNode?.is('$text'),
        // ラップメニュー
        wrap: !editor.isReadOnly && isWrapable,
      };
    }, [menuTargetNode, isPastableAsChild, isPastableAsSibling, isCopyable, editor.isReadOnly, isWrapable]);

    /**
     * データを元にElementまたはTextを生成して返す。
     * @param writer Writerオブジェクト。
     * @param data データ。
     * @returns 生成されたElementまたはTextオブジェクト。
     */
    const createElementByData = (writer: Writer, data: any) => {
      let resultElement = undefined;
      if (data.name) {
        resultElement = writer.createElement(data.name);
      } else if (data.data) {
        resultElement = writer.createText(data.data);
      } else {
        return undefined;
      }

      if (data.attributes) {
        const keys = Object.keys(data.attributes);
        for (const key of keys) {
          writer.setAttribute(key, data.attributes[key], resultElement);
        }
      }

      if (data.children) {
        for (const childData of data.children) {
          const child = createElementByData(writer, childData);
          if (child) {
            writer.insert(child, resultElement, 'end');
          }
        }
      }

      return resultElement;
    };

    /**
     * 指定された要素を更新する。
     * @param target 対象要素。
     */
    const updateElement = (target: Element) => {
      if (element == target) {
        updateComponentLocal();
      } else {
        childElementRefs.current.forEach((child, index) => {
          child.current?.updateElement(target);
        });
      }
    };

    /**
     * セクションのレベルを取得する。
     * @param element 要素。
     * @returns セクションのレベル。
     */
    const getSectionLevel = (element: Element): '2' | '3' | '4' | '5' | undefined => {
      let current = element;
      while (current.parent && !current.parent.is('model:rootElement')) {
        current = current.parent as Element;
      }

      switch (current.name) {
        case 'l2Section':
          return '2';

        case 'l3Section':
          return '3';

        case 'l4Section':
          return '4';

        case 'l5Section':
          return '5';

        default:
          return undefined;
      }
    };

    /**
     * ペーストコンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handlePasteItemClick = async (args: ItemParams<any, any>, position: 'end' | 'before' | 'after') => {
      if (!menuTargetNode) {
        return;
      }

      const elementData = await navigator.clipboard.readText();
      const elementToPasteData = JSON.parse(elementData);

      // データに埋め込んである固有情報がなければペーストしない
      if (elementToPasteData.type !== CLIPBOARD_DATA_TYPE && elementToPasteData.type !== CLIPBOARD_DATA_TYPE_PS) {
        return;
      }
      // データが配列でなければペーストしない
      if (!Array.isArray(elementToPasteData.data) || elementToPasteData.data.length === 0) {
        return;
      }

      // PSデータの場合の調整処理
      if (elementToPasteData.type === CLIPBOARD_DATA_TYPE_PS) {
        // PSデータは1つのみのはず
        if (elementToPasteData.data.length !== 1) {
          return;
        }

        // タイトルPSデータの場合はセクションレベルに合わせてh?を変更する
        if (elementToPasteData.psType === PSTYPE_TITLE) {
          // セクションレベルを取得する
          const sectionLevel = getSectionLevel(menuTargetNode as Element);
          if (!sectionLevel) {
            return;
          }

          processProperty(elementToPasteData.data[0], 'name', (obj, name) => {
            if (/^heading\d$/.test(obj[name])) {
              obj[name] = `heading${sectionLevel}`;
            }
          });
        }
      }

      editor.model.change((writer: Writer) => {
        // 自分の後に入れる場合は、最後から挿入していかないといけない
        const dataArray = position === 'after' ? elementToPasteData.data.reverse() : elementToPasteData.data;

        while (dataArray.length > 0) {
          const data = dataArray.shift();
          const elementToPaste = createElementByData(writer, data) as ModelItem.Item | undefined;
          if (elementToPaste) {
            writer.insert(elementToPaste, menuTargetNode as ModelItem.Item, position);
          }
        }
      });

      updateComponentLocal();
    };

    /**
     * ラップコンテキストメニュークリックイベントハンドラ。
     * @param args パラメータ。
     */
    const handleWrapItemClick = (args: ItemParams<any, any>) => {
      if (!menuTargetNode) {
        return;
      }

      editor.model.change((writer: Writer) => {
        const newElement = writer.createElement('htmlDiv');
        writer.wrap(
          writer.createRange(writer.createPositionBefore(menuTargetNode), writer.createPositionAfter(menuTargetNode)),
          newElement
        );

        const attributes = {
          attributes: {
            ps_no: '@@@',
            ps_version_id: '@@@',
            ps_derivation_id: '@@@',
            confirmed: '2',
          },
        };
        writer.setAttribute(
          'htmlDivAttributes',
          attributes as unknown as Record<string, string | number | boolean>,
          newElement
        );

        // 下位の要素のconfirmed属性を削除
        removeConfirmedAttribute(writer, menuTargetNode as Element);

        // ラップした要素は展開して表示
        setExpandingItems([...expandingItems, newElement]);

        // ラップした要素を選択
        setSelectedElement(newElement);
      });

      updateComponentLocal();
    };

    /**
     * 要素と配下の要素のconfirmed属性を削除する。
     * @param node 対象ノード。
     */
    const removeConfirmedAttribute = (writer: Writer, node: Node) => {
      const keys = Array.from(node.getAttributeKeys());
      for (const key of keys) {
        if (/^html.+$/.test(key)) {
          const attributes = node.getAttribute(key) as any;
          if (attributes?.attributes && 'confirmed' in attributes?.attributes) {
            const copiedAttributes = JSON.parse(JSON.stringify(attributes));
            delete copiedAttributes.attributes.confirmed;
            writer.setAttribute(key, copiedAttributes, node);
          }
        }
      }

      if (node.is('model:element')) {
        const children = Array.from(node.getChildren());
        for (const child of children) {
          removeConfirmedAttribute(writer, child as Element);
        }
      }
    };

    /**
     * ===========================
     * ==== ↓↓属性ダイアログ↓↓ ====
     * ===========================
     */

    const [attributesModalIsOpen, setAttributesModalIsOpen] = React.useState<boolean>(false);

    // 属性値編集メニュー選択時
    const handleEditAttributesItemClick = (args: ItemParams) => {
      setAttributesModalIsOpen(true);
    };

    /**
     * ===========================
     * ==== ↑↑属性ダイアログ↑↑ ====
     * ===========================
     */

    /**
     * 外部から操作できる関数。
     */
    useImperativeHandle(ref, () => ({
      /**
       * 指定された要素を更新する。
       * @param target 対象要素。
       */
      updateElement: (target: Element) => {
        if (element == target) {
          updateComponentLocal();
        } else {
          childElementRefs.current.forEach((child, index) => {
            child.current?.updateElement(target);
          });
        }
      },
    }));

    /**
     * ドラッグ中の要素を保持するステート。
     */
    const [draggingNode, setDraggingNode] = useState<Node | null>(null);

    /**
     * ドラッグ開始時に呼ばれる。
     * @param event イベント。
     */
    const handleDragStart = (event: DragStartEvent) => {
      const activeNode = event.active.data.current?.node;
      if (activeNode) {
        setDraggingNode(activeNode);
      }
    };

    /**
     * ドラッグ終了時に呼ばれる。
     * @param event イベント。
     */
    const handleDragEnd = (event: DragEndEvent) => {
      const targetNode = event.over?.data.current?.node;
      const movingNode = draggingNode; // event.activeだと取れない
      const position = event.over?.data.current?.position ?? 'before';

      if (targetNode && movingNode) {
        // 移動先が異なる場合のみ考慮
        if (targetNode !== movingNode) {
          editor.model.change((writer: Writer) => {
            writer.insert(movingNode, targetNode, position);
          });
        }
      } else {
        console.log(`targetNode: ${targetNode}`);
        console.log(`movingNode: ${movingNode}`);
      }
      setDraggingNode(null);
    };

    /**
     * バリデーションを行う。
     * @param element 要素。
     * @param definition 定義。
     * @returns バリデーション結果。
     */
    const validate = (element: Element, definition?: ElementDefinition): [boolean, string] => {
      if (!definition || !element || !definition.validate) {
        return [true, ''];
      }

      return definition.validate(element);
    };
    const elementDefinition = useMemo(() => {
      return Schema.getElementDefinition(isPs ? '$psroot' : '$root');
    }, [element]);
    const [validationResult, validationMessage] = useMemo(() => {
      return validate(element, elementDefinition);
    }, [element, childElements, elementDefinition]);

    /**
     * 開閉状態を取得。
     */
    const isOpen = useMemo(() => expandingItems.includes(element), [expandingItems.includes(element)]);

    /**
     * 表示幅を調整するための文字列。
     */
    const displayWidthString = '　'.repeat(100);

    return (
      <div
        className={
          'section-data-tree-node' +
          (rootElement == selectedElement ? ' selected' : '') +
          (validationResult ? ' validation-passed' : ' validation-failed') +
          (draggingNode !== null ? ' on-dragging' : '')
        }
        title={validationMessage}
      >
        <input type="checkbox" checked={isOpen} onChange={() => {}} />
        <span className={'node-icon'} onClick={handleToggleClick} />
        <label onClick={(e) => handleSelectClick(element, e)} onContextMenu={onContextMenu}>
          {<strong>root</strong>}
          {<span style={{ color: 'transparent' }}>{displayWidthString}</span>}
        </label>
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div
            className="children"
            style={{
              display: expandingItems.includes(element) ? 'block' : 'none',
            }}
          >
            {isOpen &&
              childElements.map((x, index) => (
                <React.Fragment key={uuidv4()}>
                  <Droppable node={x} position="before" draggingNode={draggingNode} />
                  <SectionDataTreeNode
                    ref={childElementRefs.current[index]}
                    editor={editor}
                    element={x}
                    selectedElement={selectedElement}
                    setSelectedElement={setSelectedElement}
                    expandingItems={expandingItems}
                    setExpandingItems={setExpandingItems}
                    handleSelectClick={handleSelectClick}
                    updateComponent={updateComponentLocal}
                    ghost={false}
                    draggingNode={draggingNode}
                    isDragging={draggingNode === x}
                    showContextMenu={showContextMenu}
                    viewAttributes={viewAttributes}
                    scrollIntoElement={scrollIntoElement}
                    resetScrollIntoElement={resetScrollIntoElement}
                    validationResults={validationResults}
                  />
                </React.Fragment>
              ))}

            {isOpen && <Droppable node={element} position="end" draggingNode={draggingNode} />}

            {
              /* ドラッグ中の表示用 */
              ReactDOM.createPortal(
                <DragOverlay
                  dropAnimation={null}
                  style={{
                    zIndex: 10001,
                  }}
                >
                  {draggingNode ? (
                    <SectionDataTreeNode
                      editor={editor}
                      element={draggingNode as Element}
                      selectedElement={selectedElement}
                      setSelectedElement={setSelectedElement}
                      expandingItems={expandingItems}
                      setExpandingItems={setExpandingItems}
                      handleSelectClick={handleSelectClick}
                      updateComponent={updateComponentLocal}
                      ghost={true}
                      draggingNode={null}
                      isDragging={false}
                      showContextMenu={showContextMenu}
                      viewAttributes={viewAttributes}
                      scrollIntoElement={scrollIntoElement}
                      resetScrollIntoElement={resetScrollIntoElement}
                      validationResults={validationResults}
                    />
                  ) : null}
                </DragOverlay>,
                document.body
              )
            }
          </div>
        </DndContext>

        {menuTargetNode &&
          ReactDOM.createPortal(
            <Menu id={id}>
              {/** 属性編集メニュー */}
              {menuFlags.editAttributes && (
                <Item onClick={handleEditAttributesItemClick}>
                  <i className="fas fa-list" />
                  &nbsp;{editor.isReadOnly ? t('View Attributes...') : t('Edit Attributes...')}
                </Item>
              )}
              {/** カットメニュー */}
              {(menuFlags.cut || menuFlags.copy || menuFlags.paste) && menuFlags.editAttributes && <Separator />}
              {menuFlags.cut && (
                <Item onClick={handleCompCutItemClick}>
                  <i className="fas fa-cut" />
                  &nbsp;
                  {isCompElement ? t('CompCut') : t('Cut')}
                </Item>
              )}
              {/** コピーメニュー */}
              {menuFlags.copy && (
                <>
                  <Item onClick={handleSingleCopyItemClick}>
                    <i className="far fa-copy" />
                    &nbsp;
                    {isCompElement ? t('SingleCopy') : t('Copy')}
                  </Item>
                  {isCompElement && (
                    <Item onClick={handleCompCopyItemClick}>
                      <i className="fas fa-copy" />
                      &nbsp;
                      {t('CompCopy')}
                    </Item>
                  )}
                </>
              )}
              {/** ペーストメニュー */}
              {menuFlags.paste && (
                <>
                  <Submenu
                    label={
                      <>
                        <i className="fas fa-paste" />
                        &nbsp;{t('Paste')}
                      </>
                    }
                    disabled={!(isPastableAsChild || isPastableAsSibling) || editor.isReadOnly}
                  >
                    <Item disabled={!isPastableAsChild} onClick={(args) => handlePasteItemClick(args, 'end')}>
                      {t('As Child')}
                    </Item>
                    <Item disabled={!isPastableAsSibling} onClick={(args) => handlePasteItemClick(args, 'before')}>
                      {t('Before')}
                    </Item>
                    <Item disabled={!isPastableAsSibling} onClick={(args) => handlePasteItemClick(args, 'after')}>
                      {t('After')}
                    </Item>
                  </Submenu>
                </>
              )}
              {/** ラップメニュー */}
              {menuFlags.wrap && (
                <>
                  <Item onClick={handleWrapItemClick}>
                    <i className="fas fa-expand" />
                    &nbsp;
                    {t('Div Wrap')}
                  </Item>
                </>
              )}
              {/* 削除メニュー */}
              {menuFlags.delete &&
                (menuFlags.editAttributes || menuFlags.cut || menuFlags.copy || menuFlags.paste || menuFlags.wrap) && (
                  <Separator />
                )}
              {menuFlags.delete && (
                <>
                  <Item onClick={handleDeleteItemClick} disabled={editor.isReadOnly}>
                    <i className="fas fa-circle-minus" />
                    &nbsp;
                    {t('Delete...')}
                  </Item>
                </>
              )}
              {/* 展開・閉じるメニュー */}
              {menuFlags.expandCollapse &&
                (menuFlags.editAttributes ||
                  menuFlags.cut ||
                  menuFlags.copy ||
                  menuFlags.paste ||
                  menuFlags.delete) && <Separator />}
              {menuFlags.expandCollapse && (
                <>
                  <Item onClick={handleExpandAllItemClick}>
                    <i className="fas fa-angles-down" />
                    &nbsp;
                    {t('Expand All')}
                  </Item>
                  <Item onClick={handleCollapseAllItemClick}>
                    <i className="fas fa-angles-up" />
                    &nbsp;
                    {t('Collapse All')}
                  </Item>
                </>
              )}
            </Menu>,
            document.body
          )}

        {/* Element用属性ダイアログ */}
        {menuTargetNode && menuTargetNode.is('model:element') && (
          <AttributeDialog
            editor={editor}
            node={menuTargetNode}
            isOpen={attributesModalIsOpen}
            setIsOpen={setAttributesModalIsOpen}
          />
        )}

        {/* Text用属性ダイアログ */}
        {menuTargetNode && menuTargetNode.is('$text') && (
          <TextAttributeDialog
            editor={editor}
            node={menuTargetNode}
            isOpen={attributesModalIsOpen}
            setIsOpen={setAttributesModalIsOpen}
          />
        )}

        {isDeleteConfirmDialogVisible && (
          <Modal
            active
            additionalClassName="popupfr340"
            displayCloseButton={false}
            onClose={() => {
              /* do nothing */
            }}
          >
            <div>{t('Do you want to delete it?')}</div>
            <div className="mb0">
              <button
                className="red-button"
                onClick={() => {
                  setIsDeleteConfirmDialogVisible(false);
                  handleDeleteConfirm();
                }}
              >
                {t('Yes')}
              </button>
              <button
                className="red-button ml10"
                onClick={() => {
                  setIsDeleteConfirmDialogVisible(false);
                }}
              >
                {t('No')}
              </button>
            </div>
          </Modal>
        )}
      </div>
    );
  }
);
export { SectionDataTreeRootNode };
